import { useEffect, useMemo, useRef, useState } from 'react';

import { CartProduct, ProductCard as Product } from "./types/productCard.interface.ts";
import { DeliveryPoint } from "../DeliveryPoint/DeliveryPoint.tsx";
import { ProductAvailability } from "../ProductAvailability/ProductAvailability.tsx";
import "yet-another-react-lightbox/styles.css";
import { Loader } from 'lucide-react';
import { ProductSpecs } from '../../pages/ProductPage/components/ProductSpecs/ProductSpecs.tsx';
import { ProductApplicability } from '../../pages/ProductPage/components/ProductApplicability/ProductApplicability.tsx';
import { ImgCarousel } from '../ImgCarousel/ImgCarousel.tsx';
import { ProductSearchByTransitionCompany } from '../../pages/ProductPage/components/ProductSearchByTransitionCompany/ProductSearchByTransitionCompany.tsx';
import { RootState, useAppDispatch, useAppSelector } from '@/utils/storage/stores/store.ts';
import { ProductAccompanyingItems } from '../../pages/ProductPage/components/ProductAccompanyingItems/ProductAccompanyingItems.tsx';
import { AccompanyingItem as AccompanyingItemType } from '../../pages/ProductPage/components/ProductAccompanyingItems/AccompanyingItem.interface.ts';
import { addToCartPublisher } from '@/utils/storage/extra/loaders/cart/addToCartPublisher.ts';
import { useSearchParams } from 'react-router-dom';

interface Props {
    product: Product
    updateBranch: (branchId: number) => void,
    currentBranchId?: number,
}

/**
 * Карточка товара
 * @param param
 * @returns
 */
export const ProductCard: React.FC<Props> = ({
    product,
    updateBranch,
    currentBranchId,
}) => {
    const car = useAppSelector((state: RootState) => state.user.car);
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const [openImg, setOpenImg] = useState(false);
    const [isImgLoaded, setIsImgLoaded] = useState(false);
    const [isCartProduct, setIsCartProduct] = useState(false);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const { deliveryPoint, availability } = product;
    const [currentItem, setCurrentItem] = useState<AccompanyingItemType | null>(null);
    const cartProducts = useAppSelector((state: RootState) => state.cart.cartOffer?.products || []);

    const fullTitle = useMemo(() => {
        return [product.article, product.brand, product.name].join(' ')
    }, []);

    //Установка просматриваемого товара
    useEffect(() => {
        //Проверка на то, что товар уже в корзине
        const findedItem = cartProducts.find((cartProduct: CartProduct) => cartProduct.productId === product.productId);
        setIsCartProduct(!!findedItem);

        //Установка текущего товара
        const currentItem = !deliveryPoint?.offers?.length ? null : {
            id: findedItem ? findedItem.id : deliveryPoint?.offers[0].id,
            productId: product.productId,
            name: product.name,
            brand: product.brand,
            price: findedItem ? findedItem.price : deliveryPoint?.offers[0].price,
            article: product.article,
            stock: findedItem ? findedItem.stock : deliveryPoint?.offers[0].stock || 0,
            slug: product.brandSlug,
            image: product.img,
        };
        setCurrentItem(currentItem);
    }, []);

    /** Сохранить текущий оффер перед добавлением в корзину */
    const setCurrentOfferAndAddToCart = (cartId: number, offerId: number) => {
        const currentOffer = deliveryPoint?.offers?.find(offer => offer.id === offerId);
        const currentItem = !currentOffer ? null : {
            id: currentOffer.id,
            productId: product.productId,
            name: product.name,
            brand: product.brand,
            price: currentOffer.price,
            article: product.article,
            stock: currentOffer.stock || 0,
            slug: product.brandSlug,
            image: product.img,
        };

        setCurrentItem(currentItem);

        const elementId = searchParams.get('elementId') || '';
        dispatch(addToCartPublisher({ cartId, itemId: offerId, elementId}));
    };

    return (
        <div className='bg-[#fafbfc] rounded-2xl p-4 sm:p-8 w-full max-w-5xl mb-10 shadow-md' ref={cardRef}>
            <div className='flex flex-col sm:flex-row'>
                <div className='flex flex-col w-full sm:w-1/2 md:w-1/3 mb-4 sm:mb-0 relative'>
                    {!isImgLoaded && <Loader className='w-8 h-8 animate-spin text-orange-500 absolute top-1/3 left-1/2' />}
                    <img className={isImgLoaded ? 'w-full max-w-[340px] h-[220px] sm:h-[300px] md:h-[340px] object-cover mb-2 rounded-2xl cursor-pointer transition-transform hover:scale-105 shadow-lg' : ''}
                        src={product.img}
                        alt={'Изображение ' + product.name}
                        onLoad={() => setIsImgLoaded(true)}
                        onClick={() => setOpenImg(true)} />
                </div>

                {openImg && <ImgCarousel images={[{ id: product.productId, url: product.img }]}
                    showCarousel={openImg}
                    setShowCarousel={setOpenImg} />}

                <div className='w-full sm:w-1/2 md:w-2/3 sm:pl-8 flex flex-col justify-between'>
                    <div>
                        <h1 className='text-2xl font-bold mb-2'>{fullTitle}</h1>
                        <div className='mb-2 text-base space-y-1'>
                            <div><span>Бренд:</span> <span className='font-bold'>{product.brand}</span></div>
                            <div><span>Артикул:</span> <a className='text-orange-600 underline font-bold' href={`/spares/${product.brandSlug}/${product.article}`}>{product.article}</a></div>
                            <div><span>Страна:</span> <span className='font-bold'>{product.country}</span></div>
                        </div>

                        {/* Характеристики */}
                        {product.specs && <ProductSpecs specs={product.specs} />}
                        {/* Применимость */}
                        {product.applicability && <ProductApplicability applicability={product.applicability} />}

                        <div className='border-b-2 border-orange-400 mb-2'></div>

                        {/* Доставка */}
                        {deliveryPoint && deliveryPoint.offers?.length > 0 ? <DeliveryPoint
                            point={deliveryPoint}
                            addProductToCart={setCurrentOfferAndAddToCart}
                            cardRef={cardRef}
                        /> : <p className='text-base'>К сожалению не удалось найти подходящие предложения на указанном филиале</p>}

                        {/* Наличие в филиалах */}
                        {availability && availability?.length > 0 && <ProductAvailability
                            availability={availability}
                            updateBranch={updateBranch}
                            cardRef={cardRef}
                        />}

                        {/* Доставка транспортной компанией */}
                        {deliveryPoint && !deliveryPoint?.offers?.length && !availability?.length && <ProductSearchByTransitionCompany
                            currentCity={deliveryPoint?.name}
                            branchId={deliveryPoint.id}
                            townId={deliveryPoint?.townId} />}
                    </div>
                </div>
            </div>

            {/* Сопутствующие товары */}
            {currentBranchId && currentItem && car && <ProductAccompanyingItems
                currentItem={currentItem}
                currentBranchId={currentBranchId}
                productId={product.productId}
                brandId={car.brandId}
                ssd={car.ssd}
                isCartProduct={isCartProduct}
            />}
        </div>
    );
};
